import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'
import {useTranslation} from 'react-i18next'
import {composeIcon, type IconComposeState} from '../../../common/utils/iconRenderer'
import {IconLoadError, loadIconImageFile} from './loadIconFile'

interface IconCropEditorProps {
  state: IconComposeState
  onZoomChange: (zoom: number) => void
  onOffsetChange: (offsetX: number, offsetY: number) => void
  onReset: () => void
  onImageLoaded: (image: HTMLImageElement) => void
}

const PREVIEW_PIXELS = 512
const ZOOM_MIN = 0.2
const ZOOM_MAX = 2.5
const OFFSET_RANGE = 0.5
const WHEEL_ZOOM_FACTOR = 0.0015

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value))

const IconCropEditor = ({state, onZoomChange, onOffsetChange, onReset, onImageLoaded}: IconCropEditorProps) => {
  const {t} = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{startX: number; startY: number; startOffsetX: number; startOffsetY: number} | null>(null)
  const [dropOver, setDropOver] = useState(false)
  const [dropError, setDropError] = useState<string | null>(null)

  useEffect(() => {
    const target = canvasRef.current
    if (!target) return
    const composed = composeIcon(state, PREVIEW_PIXELS)
    const ctx = target.getContext('2d')
    if (!ctx) return
    target.width = PREVIEW_PIXELS
    target.height = PREVIEW_PIXELS
    ctx.clearRect(0, 0, PREVIEW_PIXELS, PREVIEW_PIXELS)
    ctx.drawImage(composed, 0, 0)
  }, [state])

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!state.sourceImage) return
      e.preventDefault()
      const target = e.currentTarget
      target.setPointerCapture(e.pointerId)
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startOffsetX: state.offsetX,
        startOffsetY: state.offsetY,
      }
    },
    [state.sourceImage, state.offsetX, state.offsetY],
  )

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current
      if (!drag) return
      const rect = e.currentTarget.getBoundingClientRect()
      const dxFrac = (e.clientX - drag.startX) / rect.width
      const dyFrac = (e.clientY - drag.startY) / rect.height
      const nextX = clamp(drag.startOffsetX + dxFrac, -OFFSET_RANGE, OFFSET_RANGE)
      const nextY = clamp(drag.startOffsetY + dyFrac, -OFFSET_RANGE, OFFSET_RANGE)
      onOffsetChange(nextX, nextY)
    },
    [onOffsetChange],
  )

  const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (dragRef.current && e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    dragRef.current = null
  }, [])

  const handleWheel = useCallback(
    (e: ReactWheelEvent<HTMLCanvasElement>) => {
      if (!state.sourceImage) return
      const next = clamp(state.zoom * (1 - e.deltaY * WHEEL_ZOOM_FACTOR), ZOOM_MIN, ZOOM_MAX)
      onZoomChange(next)
    },
    [state.sourceImage, state.zoom, onZoomChange],
  )

  const handleZoomSlider = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onZoomChange(parseFloat(e.target.value))
    },
    [onZoomChange],
  )

  const handleDragOver = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDropOver(true)
  }, [])

  const handleDragLeave = useCallback(() => setDropOver(false), [])

  const handleDrop = useCallback(
    async (e: ReactDragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDropOver(false)
      setDropError(null)
      const file = e.dataTransfer.files[0]
      if (!file) return
      try {
        const img = await loadIconImageFile(file)
        onImageLoaded(img)
      } catch (err) {
        const key = err instanceof IconLoadError ? err.key : 'failed'
        setDropError(t(`iconGenerator.upload.${key}`))
      }
    },
    [onImageLoaded, t],
  )

  const canPan = state.sourceImage !== null
  const stageClass = `icg-crop-stage${dropOver ? ' is-dragover' : ''}`

  return (
    <div className="icg-crop">
      <div
        ref={containerRef}
        className={stageClass}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <canvas
          ref={canvasRef}
          className="icg-crop-canvas"
          style={{cursor: canPan ? 'grab' : 'default'}}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
        />
        {!state.sourceImage && (
          <div className="icg-crop-overlay">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>{t('iconGenerator.crop.dropHere')}</span>
          </div>
        )}
        {dropOver && state.sourceImage && (
          <div className="icg-crop-overlay icg-crop-overlay-replace">{t('iconGenerator.crop.dropReplace')}</div>
        )}
      </div>

      <div className="icg-crop-controls">
        <label className="icg-slider">
          <span className="icg-slider-label">{t('iconGenerator.crop.zoom')}</span>
          <input
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={0.01}
            value={state.zoom}
            onChange={handleZoomSlider}
            disabled={!canPan}
          />
          <span className="icg-slider-value">{Math.round(state.zoom * 100)}%</span>
        </label>
        <button type="button" className="icg-btn icg-btn-ghost" onClick={onReset} disabled={!canPan}>
          {t('iconGenerator.crop.reset')}
        </button>
      </div>

      {dropError && <p className="icg-error">{dropError}</p>}
    </div>
  )
}

export default IconCropEditor
